import { Component, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule, ReactiveFormsModule } from "@angular/forms"
import { MatButtonModule } from "@angular/material/button"
import { MatCardModule } from "@angular/material/card"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatInputModule } from "@angular/material/input"
import { MatSelectModule } from "@angular/material/select"
import { MatRadioModule } from "@angular/material/radio"
import { MatChipsModule } from "@angular/material/chips"
import { MatAutocompleteModule } from "@angular/material/autocomplete"
import { RouterModule, Router } from "@angular/router"
import { AuthService } from "../../services/auth.service"
import { ApiService } from "../../services/api.service"
import { SnackbarService } from "../../services/snackbar.service"
import { MatDialog } from "@angular/material/dialog"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import {
  RecordingDeclineModalComponent,
  RecordingDeclineModalResult,
} from "../recordings-list/recording-decline-modal.component"
import { HasRoleDirective } from "../../directives/has-role.directive"
import { RecordingService } from "../../services/recording.service"
import { Recording, RECORDING_STATUS_CLASS, RECORDING_STATUS_LABELS } from "../../models/recording.model"
import { OrdersService, OrderItem } from "../../services/orders.service"
import {
  getOrderDisplayAmount,
  getOrderPricingDisplay,
  getOrderCustomerDisplay,
  getOrderStationLabel,
  OrderCustomerDisplay,
  OrderPricingDisplay,
} from "../../utils/order-display-amount"
import { PermissionService } from "../../services/permission.service"
import { PromoCodeService } from "../../services/promo-code.service"
import { ScheduleDayLockoutComponent } from "../schedule-day-lockout/schedule-day-lockout.component"
import { environment } from "../../../environments/environment"

@Component({
  selector: "app-user-home",
  imports: [
    ScheduleDayLockoutComponent,
    CommonModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    FormsModule,
    MatButtonModule,
    MatInputModule,
    ReactiveFormsModule,
    RouterModule,
    MatProgressSpinnerModule,
    HasRoleDirective,
    MatRadioModule,
    MatChipsModule,
    MatAutocompleteModule,
  ],
  templateUrl: "./user-home.component.html",
  styleUrl: "./user-home.component.css",
})
export class UserHomeComponent implements OnInit {

  // Dashboard data
  recentOrders: OrderItem[] = []
  recentRecordings: Recording[] = []
  orderEligibility: any = null

  // Audio duration cache for previews (seconds by recording id)
  durations: Record<number, number> = {}

  // Loading flags
  loadingOrders = false
  loadingRecordings = false
  offersCount = 0
  loadingOffers = false
  downloadingOrderId: number | null = null

  // Status helpers for template
  customStatusLabels: Record<number, string> = {
    0: "Pending station approval",
    1: "Ready to air",
    2: "Declined"
  }
  statusClass = RECORDING_STATUS_CLASS

  constructor(
    private router: Router,
    private authService: AuthService,
    private recordingService: RecordingService,
    private ordersService: OrdersService,
    private promoCodeService: PromoCodeService,
    private permissionService: PermissionService,
    private apiService: ApiService,
    private snackbar: SnackbarService,
    private dialog: MatDialog,
  ) { }

  /**
   * "Am I station staff anywhere?" — derived from the assignment list, not the
   * global users.role column. That column holds the role of the first station the
   * account was ever added to and is never refreshed, so an advertiser who is
   * later given a station assignment kept being shown the advertiser-only view.
   * Intentionally station-agnostic: this page is not scoped to one station.
   */
  get isRegularUser(): boolean {
    return this.permissionService.isAdvertiserUserOnly()
  }

  get isStaffRole(): boolean {
    return !this.permissionService.isAdvertiserUserOnly()
  }

  /** Effective role for the selected station; a getter so it follows station switches. */
  get role(): string {
    return this.permissionService.getCurrentRole()
  }

  get isDownloadingReceipt(): boolean {
    return this.downloadingOrderId != null
  }

  downloadReceipt(orderId: number): void {
    if (this.downloadingOrderId != null) return
    this.downloadingOrderId = orderId
    this.apiService
      .authenticatedGet<{ pdf_base64: string; filename?: string }>(
        `${environment.apiUrl}/orders/${orderId}/receipt`,
      )
      .subscribe({
        next: (response) => {
          const byteCharacters = atob(response.pdf_base64)
          const byteNumbers = Array.from(byteCharacters, (c) => c.charCodeAt(0))
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: "application/pdf" })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement("a")
          link.href = url
          link.download = response.filename || `RAD-Receipt-${orderId}.pdf`
          link.click()
          window.URL.revokeObjectURL(url)
          this.downloadingOrderId = null
        },
        error: (err) => {
          console.error("Receipt download failed", err)
          this.downloadingOrderId = null
          this.snackbar.error("Failed to download receipt. Please try again.")
        },
      })
  }

  ngOnInit() {
    // Clear campaign builder state when entering home page
    localStorage.removeItem("campaignBuilderState");
    localStorage.removeItem("campaignPreview");
    localStorage.removeItem("campaignRequest");
    localStorage.removeItem("campaignPreviewDetails");
    localStorage.removeItem("wasInPreview");

    const user = this.authService.getCurrentUserInfo()

    this.fetchRecentOrders()
    this.fetchRecentRecordings()
    if (this.isAdvertiserUserOnly) {
      this.fetchOffersCount()
    }
  }

  get isAdvertiserUserOnly(): boolean {
    return this.permissionService.isAdvertiserUserOnly()
  }

  private fetchOffersCount(): void {
    this.loadingOffers = true
    this.promoCodeService.getEligibleOffersCount().subscribe({
      next: (count) => {
        this.offersCount = count
        this.loadingOffers = false
      },
      error: () => {
        this.offersCount = 0
        this.loadingOffers = false
      },
    })
  }

  private fetchRecentOrders() {
    this.loadingOrders = true
    this.ordersService.list({ limit: 6, order: "DESC" }).subscribe({
      next: (res) => {
        this.recentOrders = res.orders || []
        this.orderEligibility = res.orderEligibility
        this.loadingOrders = false
      },
      error: () => (this.loadingOrders = false),
    })
  }

  private fetchRecentRecordings() {
    this.loadingRecordings = true
    let params: any = { limit: 3, sort_by: "id", sort_order: "DESC" }
    if (this.role !== 'user') {
      params.pending = true
    }
    this.recordingService.list(params).subscribe({
      next: (res) => {
        this.recentRecordings = res.records || []
        this.loadingRecordings = false
      },
      error: () => (this.loadingRecordings = false),
    })
  }

  goToCreate() {
    this.router.navigate(["/audio"])
  }

  goToCommercial() {
    this.router.navigate(["/production-requests"])
  }

  logout() {
    this.authService.logout()
    this.router.navigate(["/login"])
  }

  formatDuration(seconds: number): string {
    if (!seconds) return "00:00"
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
  }

  onAudioMetadata(recording: Recording, event: Event) {
    const audio = event.target as HTMLAudioElement
    if (!audio) return
    const dur = isFinite(audio.duration) ? Math.round(audio.duration) : 0
    const id = recording.id ?? 0
    if (id && dur > 0) {
      this.durations[id] = dur
    }
  }

  displayDuration(recording: Recording): string {
    const id = recording.id ?? 0
    const sec = (id && this.durations[id]) ? this.durations[id] : (recording.audiolength || 0)
    return this.formatDuration(sec)
  }

  // -------- UI helpers (formatting & KPIs) --------
  formatDateTime(iso?: string): string {
    if (!iso) return "—"
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  compactNumber(n?: number): string {
    if (n === undefined || n === null) return "0"
    try {
      return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(n)
    } catch {
      return String(n)
    }
  }

  get kpiTotalImpressions(): number {
    return this.recentOrders.reduce((sum, o) => sum + (o.totalimpressions || 0), 0)
  }

  get kpiTotalAirings(): number {
    return this.recentOrders.reduce((sum, o) => sum + (o.totalairings || 0), 0)
  }

  get kpiTotalCost(): number {
    return this.recentOrders.reduce((sum, o) => sum + this.displayOrderAmount(o), 0)
  }

  displayOrderAmount(order: OrderItem): number {
    return getOrderDisplayAmount(order)
  }

  orderPricing(order: OrderItem): OrderPricingDisplay {
    return getOrderPricingDisplay(order)
  }

  // Layout helpers
  get hasOrders(): boolean {
    return this.recentOrders && this.recentOrders.length > 0
  }

  get hasRecordings(): boolean {
    return this.recentRecordings && this.recentRecordings.length > 0
  }

  get showOrderPlacedBy(): boolean {
    const r = (this.role || "").toLowerCase()
    return r === "owner" || r === "admin" || r === "station_owner" || r === "station_user"
  }

  orderStationLabel(order: OrderItem): string {
    return getOrderStationLabel(order)
  }

  orderCustomer(order: OrderItem): OrderCustomerDisplay | null {
    return getOrderCustomerDisplay(order)
  }

  // Navigation from dashboard orders
  goToOrdersList() {
    this.router.navigate(["/orders"])
  }

  openOrderSchedules(orderId: number) {
    this.router.navigate(["/managecampaigns"], { queryParams: { orderid: orderId } })
  }

  goToRecordings() {
    this.router.navigate(["/audio"])
  }

  goToRecordingsList() {
    this.router.navigate(["/recordings"])
  }

  approveRecording(recording: Recording) {
    if (!recording.id) return
    // Get station_id from station_approvals
    const stationId = recording?.station_id || 0
    this.recordingService.approve(recording.id, stationId).subscribe({
      next: () => this.fetchRecentRecordings(),
      error: (err) => console.error("Approve error", err)
    })
  }

  /** Pending card of a station the caller is assigned to (each station reviews independently). */
  canModerateRecording(recording: Recording): boolean {
    if (recording.approval_status !== 0 || recording.is_superseded) return false
    return this.permissionService.canActOnStation(recording.station_id)
  }

  declineRecording(recording: Recording) {
    if (!recording.id || !recording.station_id) return
    const ref = this.dialog.open(RecordingDeclineModalComponent, {
      width: "560px",
      maxWidth: "94vw",
      panelClass: ["app-confirm-dialog", "recording-decline-dialog"],
      autoFocus: "first-tabbable",
      data: {
        recordingName: recording.name || `Recording #${recording.id}`,
        recordingId: recording.id,
        stationId: recording.station_id,
      },
    })
    ref.afterClosed().subscribe((result: RecordingDeclineModalResult | undefined) => {
      if (!result?.success) return
      this.fetchRecentRecordings()
    })
  }
}
